import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { Field, fmtDateTime } from './FormBits.jsx';
import { SaveStatus, useSaveStatus } from './SaveStatus.jsx';

// Session 12B: Settings → Publishing (agency only). Posts for a partner_api
// client go live through the Build A Better Website Partner API. The site id
// is saved through portal_update_client's publish section; Check connection
// and Run test post queue droplet jobs (portal_publish_check / _test) that
// write pack.publish.partner.check / .test, which this tab polls. Automatic
// publishing can only be switched on once both are green for this site id.
// The key never reaches the portal: key_present is all we see.
//
// Session 57 (Wave 18): a client created as "Astro / Dave" arrives here with
// partner.auto_enable set: the droplet chains check -> sandbox test -> on by
// itself and emails Leena once. This tab shows where the chain is and can
// stop it (portal_update_client publish.auto_enable: false); it never arms it.

const MODES = [
  ['handoff', 'We deliver ready-to-post packages'],
  ['partner_api', 'We publish automatically through Build A Better Website'],
];
const BUSY = ['pending', 'running'];

function stateLine(st, what) {
  if (!st || st.state === 'stale') return { tone: 'none', text: `Not run for this site yet.` };
  if (BUSY.includes(st.state)) return { tone: 'pending', text: what === 'check' ? 'Checking the connection…' : 'Running the test post on the sandbox… (a few minutes)' };
  if (st.state === 'ok') return { tone: 'ok', text: `${what === 'check' ? 'Connected' : 'Test post passed'}${st.at ? ` · ${fmtDateTime(st.at)}` : ''}` };
  return { tone: 'err', text: st.message || (what === 'check' ? 'The check failed.' : 'The test post failed.') };
}

const MARK = { ok: '✓', warn: '!', fail: '✗' };

export default function PublishingTab({ client, onSaved }) {
  const isDL = client.client_id === 'digitallotus';
  const [live, setLive] = useState(null);           // {publish, publish_adapter} while polling
  const publish = (live ? live.publish : client.pack?.publish) || {};
  const adapter = live ? live.publish_adapter : client.publish_adapter;
  const part = publish.partner || {};
  const [site, setSite] = useState(part.site_id || '');
  const [mode, setMode] = useState(adapter === 'partner_api' ? 'partner_api' : 'handoff');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  useEffect(() => { setLive(null); }, [client]);
  useEffect(() => { setSite((client.pack?.publish?.partner || {}).site_id || ''); setMode(client.publish_adapter === 'partner_api' ? 'partner_api' : 'handoff'); }, [client]);

  const check = part.check || null;
  const test = part.test || null;
  const working = BUSY.includes(check?.state) || BUSY.includes(test?.state);
  const forSite = (st) => st && st.state === 'ok' && st.site_id === part.site_id;
  const ready = forSite(check) && forSite(test) && (check.sites || []).includes(part.site_id);
  const siteDirty = site.trim().toLowerCase() !== (part.site_id || '');
  // Session 57: the automatic chain armed by Add client
  const auto = part.auto_enable === true;
  const autoStep = check?.state === 'ok'
    ? (test?.state === 'ok' ? 'Both passed; switching automatic publishing on'
      : test?.state === 'error' ? 'The test post failed; ending' : 'Connection OK; running the test post on the sandbox')
    : check?.state === 'error' ? 'The connection check failed; ending' : 'Checking the connection';

  // Everything polls until the job finishes (only the publish config, so
  // nothing else on the page is reset).
  const onSavedRef = useRef(onSaved);   // pass 2 LOW4: the parent passes a new arrow every render
  onSavedRef.current = onSaved;
  useEffect(() => {
    if (!working && !auto) return undefined;   // Session 57: keep polling while the chain runs
    let cancelled = false;
    const wasAdapter = client.publish_adapter;
    const t = setInterval(async () => {
      const { data } = await supabase.from('v_portal_client_settings')
        .select('publish:pack->publish, publish_adapter').eq('client_id', client.client_id).maybeSingle();
      if (cancelled || !data) return;
      const nowAuto = ((data.publish || {}).partner || {}).auto_enable === true;
      if (data.publish_adapter !== wasAdapter || nowAuto !== auto) {
        // Session 57 (review M3): the chain switched the row on (or ended): the
        // whole client reloads so the mode radios and Edit client agree with it.
        cancelled = true; clearInterval(t);
        setLive(null);
        try { await onSavedRef.current(); } catch (e) { setBanner({ tone: 'err', text: `Reload the page to see where it stands (${e.message || e}).` }); }
        return;
      }
      setLive({ publish: data.publish || {}, publish_adapter: data.publish_adapter });
    }, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [working, auto, client.client_id, client.publish_adapter]);

  // Session 24: the two saves (site, mode) also show the shared Saved / Couldn't save line.
  const siteSaved = useSaveStatus();
  const modeSaved = useSaveStatus();

  async function call(fn, okText, saved = null) {
    setBusy(true); setBanner(null); if (saved) saved.start();
    const { error } = await fn();
    setBusy(false);
    if (error) {
      if (saved) { saved.fail(error.message); return false; }
      setBanner({ tone: 'err', text: error.message }); return false;
    }
    if (saved) saved.ok();
    if (okText) setBanner({ tone: 'ok', text: okText });
    setLive(null);
    await onSaved();
    return true;
  }

  const saveSite = () => call(() => supabase.rpc('portal_update_client', { p_client_id: client.client_id,
    p_patch: { publish: { provider: 'bbw', site_id: site.trim().toLowerCase() } } }), 'Now run Check connection.', siteSaved);
  const runCheck = () => call(() => supabase.rpc('portal_publish_check', { p_client_id: client.client_id }), 'Checking the connection.');
  const runTest = () => call(() => supabase.rpc('portal_publish_test', { p_client_id: client.client_id }), 'Test post started on the sandbox.');
  // Session 57: Stop. The RPC says "automatic switch-on stopped" only when the
  // flag was still there; if the chain finished in the last poll gap, say that
  // instead of claiming it stopped (review L2).
  async function stopAuto() {
    setBusy(true); setBanner(null);
    const { data, error } = await supabase.rpc('portal_update_client', { p_client_id: client.client_id,
      p_patch: { publish: { auto_enable: false } } });
    setBusy(false);
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    const stopped = /stopped/.test(String(data || ''));
    setBanner(stopped
      ? { tone: 'ok', text: 'Stopped. Posts are delivered as ready-to-post packages until you switch automatic publishing on below.' }
      : { tone: 'info', text: 'It had already finished. This is where it stands now.' });
    setLive(null);
    await onSaved();
  }
  const saveMode = () => call(() => supabase.rpc('portal_update_client', { p_client_id: client.client_id,
    p_patch: { publish: { adapter: mode } } }),
  mode === 'partner_api' ? 'Automatic publishing is on. Approved posts go live on their publish date.' : 'Back to ready-to-post packages.', modeSaved);

  if (isDL) {
    return (
      <section className="review-section">
        <h2>Publishing</h2>
        <p className="hint" style={{ marginTop: 0 }}>Digital Lotus publishes to its own site from the droplet. Nothing to set here.</p>
      </section>
    );
  }
  if (adapter === 'lovable') {
    return (
      <section className="review-section">
        <h2>Publishing</h2>
        <div className="analytics-status analytics-status-ok" role="status">
          <span>Lovable: we commit to the synced GitHub repo, publish, and verify the live URL. This is set up on the droplet and can't be changed here.</span>
        </div>
      </section>
    );
  }

  const chk = stateLine(check, 'check');
  const tst = stateLine(test, 'test');
  const modeChanged = mode !== (adapter === 'partner_api' ? 'partner_api' : 'handoff');

  return (
    <>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}

      {auto && (
        <section className="review-section">
          <h2>Switching on automatically</h2>
          <div className="analytics-status analytics-status-pending" role="status">
            <span className="spinner spinner-sm" aria-hidden="true" />
            <span>{autoStep}. Set from Add client (Astro / Dave): connection check, then a test post on the sandbox, then automatic publishing switches itself on. Leena gets one email when it finishes, either way. Until then posts are delivered as ready-to-post packages.</span>
            <button type="button" className="btn" onClick={stopAuto} disabled={busy}>Stop</button>
          </div>
        </section>
      )}

      <section className="review-section">
        <h2>How posts go live</h2>
        <div className="field approval-fields" role="radiogroup" aria-label="How posts go live">
          {MODES.map(([k, label]) => {
            const locked = k === 'partner_api' && !ready && adapter !== 'partner_api';
            return (
              <label key={k} className="check approval-option">
                <input type="radio" name="publish-mode" value={k} checked={mode === k} disabled={busy || locked}
                       onChange={() => setMode(k)} />
                <span>
                  <span className="approval-option-label">{label}</span>
                  {locked && <span className="field-hint">Available once Check connection and the test post are both green.</span>}
                </span>
              </label>
            );
          })}
        </div>
        {adapter !== 'partner_api' && !['handoff', 'partner_api'].includes(adapter) && (
          <p className="hint">Right now this client is set to “{adapter}” (Edit client → Setup).</p>
        )}
        <div className="review-actions">
          <button type="button" className="btn btn-primary" onClick={saveMode}
                  disabled={busy || !modeChanged || (mode === 'partner_api' && !ready)}>
            {mode === 'partner_api' ? 'Turn on automatic publishing' : 'Switch to ready-to-post packages'}
          </button>
          <SaveStatus status={modeSaved.status} />
        </div>
      </section>

      <section className="review-section">
        <h2>Build A Better Website</h2>
        <Field label="Site" hint="The site id Dave gave us, e.g. bespokeholidaylighting.com">
          <div className="inline-fields" style={{ alignItems: 'center' }}>
            <input value={site} onChange={(e) => setSite(e.target.value)} placeholder="bespokeholidaylighting.com"
                   disabled={busy || adapter === 'partner_api'} autoCapitalize="off" spellCheck={false} />
            <button type="button" className="btn" onClick={saveSite} disabled={busy || !siteDirty || !site.trim()}>Save site</button>
            <SaveStatus status={siteSaved.status} />
          </div>
        </Field>
        {adapter === 'partner_api' && <p className="hint">Switch to ready-to-post packages first to change the site.</p>}
        <p className="hint" style={{ marginTop: 0 }}>
          {part.key_present
            ? 'Partner key saved on the droplet ✓'
            : <>Not saved yet: run <code>tools/partner_secret.sh bbw</code> in a droplet shell.</>}
        </p>
      </section>

      <section className="review-section">
        <h2>Check connection</h2>
        <div className={`analytics-status analytics-status-${chk.tone}`} role="status">
          {chk.tone === 'pending' && <span className="spinner spinner-sm" aria-hidden="true" />}
          <span>{chk.text}</span>
          <button type="button" className="btn" onClick={runCheck} disabled={busy || working || !part.site_id || siteDirty}
                  title={siteDirty ? 'Save the site first.' : undefined}>Check connection</button>
        </div>
        {check?.findings?.length > 0 && !BUSY.includes(check.state) && (
          <ul className="upload-list" style={{ marginTop: 10 }}>
            {check.findings.map((f, i) => (
              <li key={i}><strong aria-label={f.level}>{MARK[f.level] || '·'}</strong> {f.label}{f.detail ? `: ${f.detail}` : ''}</li>
            ))}
          </ul>
        )}
        {part.post_url_pattern && <p className="hint">Live URL pattern: <code>{part.post_url_pattern}</code></p>}
      </section>

      <section className="review-section">
        <h2>Run test post (sandbox)</h2>
        <p className="hint" style={{ marginTop: 0 }}>Publishes a sample post on the private, noindexed sandbox, checks our schema, changes the title, then removes it. The live site is never touched.</p>
        <div className={`analytics-status analytics-status-${tst.tone}`} role="status">
          {tst.tone === 'pending' && <span className="spinner spinner-sm" aria-hidden="true" />}
          <span>{tst.text}</span>
          <button type="button" className="btn" onClick={runTest} disabled={busy || working || !forSite(check)}
                  title={!forSite(check) ? 'Check connection has to pass first.' : undefined}>Run test post</button>
        </div>
        {test?.url && !BUSY.includes(test.state) && (
          <p className="hint">Preview: <a href={test.url} target="_blank" rel="noreferrer">{test.url}</a> (removed again at the end of the test)</p>
        )}
      </section>
    </>
  );
}
